import React, { useState, useEffect, useRef } from 'react';
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { FaUser } from 'react-icons/fa';
import Popup from '../common/popup/Popup';
import useApi from '../../Api';
import { validateTextInput } from '../common/TextValidator';
import { useAuth } from '../../AuthContext';
import { toast } from 'react-toastify';
import '../../styles/components/user/UsernameModal.css';
const UsernameModal = ({ show, handleClose, editMode = false, initialUsername = '' }) => {
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(true);
  const [localValidationError, setLocalValidationError] = useState('');
  const [availabilityError, setAvailabilityError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const apiClient = useApi();
  const { refreshToken } = useAuth();
  const debounceRef = useRef(null);
  useEffect(() => {
    if (show && initialUsername) {
      setUsername(initialUsername);
    }
  }, [show, initialUsername]);
  useEffect(() => {
    if (username.trim().length > 0) {
      const validation = validateTextInput(username, {
        field_name: 'Username',
        maxLength: 20,
        minLength: 3,
        allowSpecialChars: true,
        allowedSpecialChars: '-_'
      });
      if (!validation.isValid) {
        setLocalValidationError(validation.errors.join(' '));
        setUsernameAvailable(false);
        setAvailabilityError('');
      } else {
        setLocalValidationError('');
        if (username.trim().length >= 3) {
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(async () => {
            try {
              const response = await apiClient.post('/check-username-available', { username: username.trim() });
              if (!response.data.available) {
                setUsernameAvailable(false);
                setAvailabilityError('This username is not available.');
              } else {
                setUsernameAvailable(true);
                setAvailabilityError('');
              }
            } catch (err) {
              setUsernameAvailable(true);
              setAvailabilityError('Error checking username availability.');
            }
          }, 500);
        }
      }
    } else {
      setUsernameAvailable(true);
      setLocalValidationError('');
      setAvailabilityError('');
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [username, apiClient]);
  useEffect(() => {
    if (!show) {
      setUsername('');
      setError('');
      setLocalValidationError('');
      setAvailabilityError('');
      setUsernameAvailable(true);
      setLoading(false);
      setShowConfirmModal(false);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    }
  }, [show]);
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);
  const submitUpdate = async () => {
    setLoading(true);
    try {
      await apiClient.post('/update-username', { username: username.trim() });
      await refreshToken();
      toast.success('Username updated successfully.', {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setShowConfirmModal(false);
      handleClose();
    } catch (err) {
      let errorMessage = 'Failed to update username.';
      if (err.response && err.response.data && err.response.data.detail) {
        errorMessage = Array.isArray(err.response.data.detail)
          ? err.response.data.detail.map(e => e.msg).join(' ')
          : err.response.data.detail;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const checkResponse = await apiClient.post('/check-username-available', { username: username.trim() });
    if (!checkResponse.data.available) {
      setError('This username is not available.');
      return;
    }
    const validation = validateTextInput(username, {
      field_name: 'Username',
      maxLength: 20,
      minLength: 3,
      allowSpecialChars: true,
      allowedSpecialChars: '-_'
    });
    if (!validation.isValid) {
      setError(validation.errors.join(' '));
      return;
    }
    if (!editMode) {
      await submitUpdate();
    } else {
      setShowConfirmModal(true);
    }
  };
  const handleChange = (e) => {
    setUsername(e.target.value);
    if (error) setError('');
  };
  return (
    <>
      {show && (
        <Popup title="Update Username" onCancel={handleClose} className="popup-modal username-modal">
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="formUsername" className="mb-3">
              <InputGroup>
                <InputGroup.Text className="input-icon"><FaUser /></InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Enter Username"
                  value={username}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  maxLength={20}
                />
              </InputGroup>
              {(error || localValidationError || availabilityError) && (
                <div className="text-danger mt-1">
                  {error || localValidationError || availabilityError}
                </div>
              )}
            </Form.Group>
            <div className="d-grid gap-2">
              <Button className="btn-neutral" type="submit" disabled={loading || localValidationError || availabilityError || error}>
                {loading ? <Spinner animation="border" size="sm" /> : 'Update Username'}
              </Button>
            </div>
          </Form>
        </Popup>
      )}
      {showConfirmModal && (
        <Popup
          title="Confirm Update Username"
          message={`Are you sure you want to update your username to "${username.trim()}"?`}
          onConfirm={submitUpdate}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}
    </>
  );
};
export default UsernameModal;
